import {
  gettext,
  interpolate,
  markTranslated,
} from "@pg-flex/shared-js/utils/i18n";
import {
  DateTime,
  DateTimeMaybeValid,
  DateTimeUnit,
  DurationUnit,
  Settings,
} from "luxon";

export function setup() {
  Settings.defaultLocale = "en-US";
}

export function isSameTime(a: DateTime, b: DateTime) {
  // DateTime.isEquals wants same time, same tz
  // DateTime.hasSame ignores the tz (so the same time in 2 different tz are not considered the same)
  return +a === +b;
}

export function hasSame(
  unit: DateTimeUnit,
  a: DateTime | null | undefined,
  b: DateTime | null | undefined
) {
  if (a == null || b == null) return false;

  return a.hasSame(b, unit);
}

export function isSameDayOrAfter(a: DateTime, b: DateTime) {
  return a.startOf("day") >= b.startOf("day");
}

export function isSameDayOrBefore(a: DateTime, b: DateTime) {
  return a.startOf("day") <= b.startOf("day");
}

export function truncDiff(a: DateTime, b: DateTime, unit: DurationUnit) {
  // Equivalent to dayjs(a).diff(b, unit)
  return Math.trunc(a.diff(b, unit).as(unit));
}

export function toDateTime(date: string | Date | DateTime) {
  if (typeof date === "string") return DateTime.fromISO(date);
  if (date instanceof Date) return DateTime.fromJSDate(date);
  return date;
}

export function isInRange(
  date: DateTime | null | undefined,
  [min, max]: [DateTime | null | undefined, DateTime | null | undefined]
) {
  if (date == null) return true; // by convention
  if (min != null && date < min) return false; // to early
  if (max != null && date > max) return false; // to late
  return true;
}

export function clone(date: DateTime): DateTimeMaybeValid;
export function clone<U extends null | undefined>(date: U): U;
export function clone(date: DateTime | null | undefined) {
  if (date == null) return date;
  return DateTime.fromJSDate(date.toJSDate());
}

export function relativeDateTime(date: DateTime) {
  // If today or yesterday, display the time
  // otherwise, use calendar days for relative time.
  const today = DateTime.now().startOf("day");
  const yesterday = today.minus({ days: 1 });
  const time = markTranslated(date.toFormat("HH:mm"));
  if (date.hasSame(today, "day")) {
    return interpolate(gettext("today at %(time)s"), { time });
  } else if (date.hasSame(yesterday, "day")) {
    return interpolate(gettext("yesterday at %(time)s"), { time });
  } else {
    // We don't want to use luxon's toRelativeCalendar because we don't want "last month/year" when only 2 days have passed
    // Instead, we compare to the start of the given date which give us 2 days ago even if the real diff is 1.1 days for instance.
    return markTranslated(date.startOf("day").toRelative() ?? "");
  }
}
